import { useEffect, useRef } from 'react';
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { getBankCodeByAccountNumber } from 'utils';
import type { IBanks } from './useFetchBanks';

interface BankAccountFields extends FieldValues {
  bank_account_number: string;
  bank_account_type: string;
}

type PrefillFormMethods<TFieldValues extends BankAccountFields> = Pick<
  UseFormReturn<TFieldValues>,
  'watch' | 'getValues' | 'setValue' | 'clearErrors'
>;

type AccountType = 'saving_account' | 'current_account';

const BCP_BANK_CODE = '002';
// Positions 7-8 of the CCI (1-based) encode the BCP account type
const BCP_ACCOUNT_TYPE_MARKER_START = 6;
const BCP_ACCOUNT_TYPE_MARKER_END = 8;
const BCP_CURRENT_ACCOUNT_MARKER = '00';
const BCP_SAVING_ACCOUNT_MARKER = '1';

const DEFAULT_ACCOUNT_TYPE_BY_BANK_CODE: Record<string, AccountType> = {
  '003': 'saving_account',
};

const getBcpAccountType = (accountDigits: string): AccountType | undefined => {
  const marker = accountDigits.substring(BCP_ACCOUNT_TYPE_MARKER_START, BCP_ACCOUNT_TYPE_MARKER_END);

  if (marker === BCP_CURRENT_ACCOUNT_MARKER) return 'current_account';
  if (marker.startsWith(BCP_SAVING_ACCOUNT_MARKER)) return 'saving_account';
  return undefined;
};

const getSuggestedAccountType = (banks: IBanks[], accountNumber?: string): AccountType | undefined => {
  const bankCode = getBankCodeByAccountNumber(banks, accountNumber);
  if (!bankCode) return undefined;

  if (bankCode === BCP_BANK_CODE) return getBcpAccountType(accountNumber?.replace(/\D/g, '') ?? '');
  return DEFAULT_ACCOUNT_TYPE_BY_BANK_CODE[bankCode];
};

export const usePrefillAccountTypeByBank = <TFieldValues extends BankAccountFields>(
  { watch, getValues, setValue, clearErrors }: PrefillFormMethods<TFieldValues>,
  banks: IBanks[],
) => {
  const prefilledAccountTypeRef = useRef<string>('');

  useEffect(() => {
    const accountTypeField = 'bank_account_type' as Path<TFieldValues>;

    const subscription = watch((values, { name }) => {
      if (name !== 'bank_account_number') return;

      const currentAccountType: string = getValues(accountTypeField) ?? '';
      const isSelectedByUser = Boolean(currentAccountType) && currentAccountType !== prefilledAccountTypeRef.current;
      if (isSelectedByUser) return;

      const suggestedAccountType = getSuggestedAccountType(banks, values.bank_account_number) ?? '';
      prefilledAccountTypeRef.current = suggestedAccountType;
      if (suggestedAccountType === currentAccountType) return;

      setValue(accountTypeField, suggestedAccountType as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldDirty: true,
      });
      if (suggestedAccountType) clearErrors(accountTypeField);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, getValues, setValue, clearErrors, banks]);
};
